import React, { Fragment, useState, useRef, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { PromptInfo, PromptVersionInfo, Targeting, PromptType, OutputSchemaType, AppSettings } from '../../types/settings';
import { stepTypeFamily, getPromptInputMeta, GRADE_OPTIONS, DIFFICULTY_OPTIONS, PROFICIENCY_OPTIONS, REGISTRABLE_PROGRAMME_OPTIONS, QUESTION_TYPE_OPTIONS, PROMPT_OUTPUT_SCHEMA, OUTPUT_SCHEMA_TYPES } from '../../types/settings';
import { QUESTION_SUBJECT_OPTIONS } from '@padcanvas/shared';
import {
  promptTypeLabel, PROMPT_TYPE_SORT_INDEX,
  PROMPT_TYPE_GROUPED_OPTIONS, PROMPT_TYPE_GROUPS,
} from '../../types/promptTypeLabels';
import { findHighCardinalityPromptInputs } from '@padcanvas/shared';
import { DataTable, SearchFilterBar, formatDateMonthDay, type ColumnDef, type FilterDef, type SortState, TabStrip, JsonDiffViewer, TextDiffViewer } from '@shared-ui/components';
import { BackButton } from '@shared-ui/components';
import { useReturnLocation } from '@shared-ui/navigation';
import { Pipelines } from './Pipelines';
import { CostCaps } from './CostCaps';
import { Providers } from './Providers';
import { ProviderTieredSets } from './ProviderTieredSets';
import { ProviderParallelSets } from './ProviderParallelSets';
import './Prompts.css';

type PromptsPageTab = 'prompts' | 'pipelines' | 'cost-caps' | 'providers' | 'tiered-sets' | 'parallel-sets';

const DEFAULT_TEMPLATE = `You are an educational AI assistant evaluating a student's solution.

## Question
Subject: {{q.subject}}
Topic: {{q.topic}}
Difficulty: {{q.difficulty}}

Question: {{q.text}}

## Student's Solution
{{std.imageAnswer}}
{{std.textAnswer}}

## Your Task
Analyze the student's solution and provide feedback in JSON format.

## Guidelines
- Score from 0-10 (10 = perfect, 0 = no attempt)
- Be encouraging but honest
- Provide specific, actionable feedback

Respond ONLY with the JSON object.`;

const DEFAULT_HINT_TEMPLATE = `You are a patient tutor helping a student solve a {{q.subject}} problem.

**Question:** {{q.text}}
**Topic:** {{q.topic}}

**Your Task:**
Provide ONE helpful hint that guides them toward the solution WITHOUT giving away the answer.

Return a JSON object with "your_progress" (student-facing, second-person — start with "You have ..."), "detected_state", "detected_issue", "difficulty", "hint", "should_allow_next_hint", and "confidence" fields.`;

const DEFAULT_EXTRACT_STEPS_TEMPLATE = `You are an educational AI assistant that extracts solution steps from a student's handwritten answer.

## Context
Subject: {{q.subject}}
Topic: {{q.topic}}

## Student's Handwritten Answer
{{std.imageAnswer}}

## Your Task
Extract and list the solution steps the student has written. Return a structured representation of each step.`;

const DEFAULT_TEXTCHAT_GREET_TEMPLATE = `You are a patient, encouraging AI tutor helping a student with {{q.subject}} — {{q.topic}}.

## Context
**Question:** {{q.text}}
**Correct Answer:** {{q.correctAnswer}}
**Student's Attempt:** {{std.assessment}} (score: {{std.score}}/10)
**Student's Steps:**
{{std.stepsAnalysis}}
**Proficiency Level:** {{conv.proficiency}}

## Your Role
This is the START of a tutoring conversation. The student wants to discuss this question.

1. Greet them warmly and briefly acknowledge what they got right (if anything)
2. Ask ONE focused question to understand what they're confused about
3. Keep your response concise (2-3 sentences) — this will be spoken aloud

## Proficiency Adaptation
- LOW: Very simple language, encouraging tone, break everything into small steps
- MEDIUM: Standard explanations, some technical terms
- HIGH: Can use technical language, go deeper

## Guardrails
- Stay focused on this question and its concepts
- Do NOT give the full answer — guide them to discover it
- Keep responses short for voice conversation`;

const DEFAULT_TEXTCHAT_GUIDE_TEMPLATE = `You are a patient, encouraging AI tutor helping a student with {{q.subject}} — {{q.topic}}.

## Context
**Question:** {{q.text}}
**Correct Answer:** {{q.correctAnswer}}
**Expected Solution:**
{{sol.steps}}
**Student's Attempt:** {{std.assessment}} (score: {{std.score}}/10)
**Student's Steps:**
{{std.stepsAnalysis}}
**Proficiency Level:** {{conv.proficiency}}
**Turn:** {{conv.turnNumber}} of {{conv.maxTurns}} ({{conv.turnsRemaining}} remaining)

## Your Role
Continue the Socratic dialogue. Based on the student's latest message:

1. If they answered your question correctly — affirm and move to the next concept gap
2. If they're partially right — acknowledge what's correct, then ask a guiding question about the gap
3. If they're stuck — give a small hint (not the answer) and ask a simpler version of the question
4. If they share their canvas — analyze their work and comment on what you see

## Guidelines
- Use the Socratic method: ask guiding questions, don't lecture
- Build on what they got RIGHT before addressing mistakes
- One concept at a time — don't overwhelm
- Keep responses to 2-4 sentences (spoken aloud)
- Use analogies appropriate for {{conv.proficiency}} level
- After explaining a concept, verify understanding with a quick check question

## Proficiency Adaptation
- LOW: Very simple language, more analogies, smaller steps, more encouragement
- MEDIUM: Standard explanations, some technical terms, moderate depth
- HIGH: Can use technical language, go deeper into theory, discuss edge cases

## Guardrails
- Stay focused on this question and related concepts only
- If student asks off-topic questions, gently redirect
- Do NOT solve unrelated problems
- If the student understands the concept, suggest they try a similar problem`;

const DEFAULT_TEXTCHAT_SUMMARIZE_TEMPLATE = `You are a patient AI tutor wrapping up a discussion about {{q.subject}} — {{q.topic}}.

## Context
**Question:** {{q.text}}
**Correct Answer:** {{q.correctAnswer}}
**Proficiency Level:** {{conv.proficiency}}

## Your Task
Summarize this tutoring conversation:

1. What concepts were discussed
2. What the student understood well
3. Where they still need practice
4. One specific suggestion for what to study next

Keep it encouraging and constructive. Format as a brief, friendly summary (3-5 sentences).

At the end, list the key concepts covered as a comma-separated list on a new line starting with "Concepts covered:"`;


const DEFAULT_CONCEPT_LAB_TEMPLATE = `You are an educational AI assistant creating a deep-learning experience for a student who just solved a problem.

## Question Context
Subject: {{q.subject}}
Topic: {{q.topic}}
Difficulty: {{q.difficulty}}
Question Type: {{q.type}}
Target Exam: {{std.targetExamForSubmission}}

**Question:** {{q.text}}
**Correct Answer:** {{q.correctAnswer}}

## Solution Steps
{{sol.steps}}

## Student's Performance
**Overall Assessment:** {{std.assessment}}
**Score:** {{std.score}}/10
**Student Grade:** {{std.class}}
**Proficiency Level:** {{conv.proficiency}}

**Step-by-Step Analysis:**
{{std.stepsAnalysis}}

## Your Task
Generate a personalized learning experience to help the student deeply understand the concepts. Return a JSON object with these fields:

### 1. conceptSpotlight (string, markdown with LaTeX)
A concise explanation (3-5 sentences) of the core concept(s) tested by this question, tailored to the student's proficiency level. Focus on the "why" behind the concept, not just the formula. Use LaTeX for math (e.g., \\(F = ma\\)).

### 2. personalizedNarrative (string, markdown with LaTeX)
A 2-4 sentence narrative that connects the student's specific solution to the concept:
- If incorrect/partial: Explain the specific misconception, why it's wrong, and the correct way of thinking. Be encouraging.
- If correct: Affirm what they did well and highlight any particularly good problem-solving choices.
Do NOT repeat the step-by-step analysis — instead synthesize and connect the dots.

### 3. probingQuestions (array of 2-3 objects)
Quick conceptual questions (10-20 seconds each, NOT computation problems) that test whether the student truly understood. Each object:
- "id": unique string (e.g., "pq1", "pq2")
- "question": the question text (markdown, may include LaTeX)
- "options": array of 4 answer choices
- "correctOptionIndex": 0-based index of the correct answer
- "explanation": why the correct answer is right (2-3 sentences)

Make questions that probe understanding, not just memory. Vary difficulty: one easier, one harder.

### 4. mistakeTargetedQuestions (array of 1-2 objects)
Practice questions that specifically target the student's weak area. Each object:
- "source": always "generated"
- "questionId": null
- "questionText": a full practice question (markdown with LaTeX)
- "relevanceReason": why this question helps address the student's mistake/weakness

### 5. relatedConcepts (array of 2-3 objects)
Adjacent topics the student should explore. Each object:
- "conceptName": the concept/topic name
- "relationship": how it relates (e.g., "same technique", "prerequisite", "extension")
- "briefDescription": 1-2 sentences on what it is and why it matters

## Response Format
Respond ONLY with the JSON object. No markdown code fences. Ensure all LaTeX is properly escaped for JSON strings.`;

/** Output schemas offered in the Schema filter — the canonical tuple itself,
 *  so a schema added in shared/promptTypes.ts appears here without an edit. */
const OUTPUT_SCHEMA_OPTIONS: readonly OutputSchemaType[] = OUTPUT_SCHEMA_TYPES;

// ── Source badge for the prompt-versioning provenance fields ──────────────
// Phase 5 of the prompt-versioning rollout. See
// docs/FeatureDesigns/PipelineInfra/prompt-versioning.md.

interface SourceProvenance {
  source?: 'git' | 'admin';
  gitSha?: string;
  activatedAt?: string;
  activatedBy?: string;
  reason?: string;
}

/** The page-level tabs. Rendered in two places (prompts vs. the other
 *  views), so the list lives here rather than being written out twice. */
const PROMPTS_PAGE_TABS = [
  { key: 'prompts', label: 'Prompts' },
  { key: 'pipelines', label: 'Pipelines' },
  // A cap is a property of an operation's execution, so it belongs beside
  // Pipelines — not buried in the extraction card on Settings → App Defaults,
  // where the per-operation grid covered every operation in the system.
  { key: 'cost-caps', label: 'Cost Caps' },
  { key: 'providers', label: 'Providers' },
  { key: 'tiered-sets', label: 'Tiered Sets' },
  { key: 'parallel-sets', label: 'Parallel Sets' },
] as const;

function SourceBadge({ source, gitSha, activatedAt, activatedBy, reason, compact }: SourceProvenance & { compact?: boolean }) {
  if (!source) return null;
  const isGit = source === 'git';
  const tooltip = isGit
    ? (gitSha ? `git deploy ${gitSha.slice(0, 12)}${activatedAt ? ` at ${activatedAt}` : ''}` : 'git')
    : `admin override${activatedAt ? ` since ${activatedAt}` : ''}${activatedBy ? ` by ${activatedBy}` : ''}${reason ? ` — ${reason}` : ''}`;
  const label = isGit ? 'git' : 'admin';
  const bg = isGit ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.18)';
  const fg = isGit ? '#22c55e' : '#f59e0b';
  const border = isGit ? 'rgba(34, 197, 94, 0.35)' : 'rgba(245, 158, 11, 0.45)';
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: compact ? '1px 6px' : '2px 8px',
        borderRadius: 4,
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        fontSize: compact ? 9 : 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        lineHeight: 1.4,
        marginLeft: 6,
        verticalAlign: 'middle',
      }}
    >
      {label}
    </span>
  );
}

export function Prompts() {
  const queryClient = useQueryClient();
  const [pageTab, setPageTab] = useState<PromptsPageTab>('prompts');
  // Set when a provider/set name is clicked on the Pipelines tab, so the
  // destination lands on the thing that was asked about rather than a full list.
  const [providerFocus, setProviderFocus] = useState<string | undefined>();
  const goToProviderTab = (tab: PromptsPageTab, focus?: string) => {
    setProviderFocus(focus);
    setPageTab(tab);
  };
  // Filters for the Prompts DataTable. Stored as plain strings for
  // SearchFilterBar (which works in `Record<string, string>`); empty string
  // = "no filter for this column". `search` runs over name + description.
  const [tableFilters, setTableFilters] = useState<{ type: string; schema: string; status: string; search: string }>({
    type: '', schema: '', status: '', search: '',
  });
  const [tableSort, setTableSort] = useState<SortState>({ sortBy: 'type', sortOrder: 'asc' });
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  // Track the list panel's pixel width to drop low-priority columns when it
  // narrows. Updated via ResizeObserver below.
  const listPanelRef = useRef<HTMLDivElement | null>(null);
  const [listPanelWidth, setListPanelWidth] = useState<number>(0);
  // `?selected=<promptId>` deep-links into a specific prompt. Used by
  // detail-page links from Submission / Hint / Concept-Lab pages (see
  // shared-ui/operation-detail/defaultRunColumns and IdControl `prompts`
  // capability). Two-way sync: URL → state on mount + history navigation,
  // state → URL when the user clicks a different prompt within the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFromUrl = searchParams.get('selected');
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(selectedFromUrl);
  // URL → state: keep the selection in sync when the user uses browser
  // back/forward or pastes a new URL.
  useEffect(() => {
    if (selectedFromUrl !== selectedPromptId) {
      setSelectedPromptId(selectedFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFromUrl]);
  // state → URL: reflect in-page selections in the address bar so links
  // are shareable. `replace: true` keeps history clean.
  useEffect(() => {
    const current = searchParams.get('selected');
    if (selectedPromptId && selectedPromptId !== current) {
      const next = new URLSearchParams(searchParams);
      next.set('selected', selectedPromptId);
      setSearchParams(next, { replace: true });
    } else if (!selectedPromptId && current) {
      const next = new URLSearchParams(searchParams);
      next.delete('selected');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromptId]);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [copyDialogTarget, setCopyDialogTarget] = useState<PromptType | null>(null);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [creatingType, setCreatingType] = useState<PromptType>('analyze_image_text_objective');
  const [isEditing, setIsEditing] = useState(false);
  const [editedUserPrompt, setEditedUserPrompt] = useState('');
  const [editedSystemPrompt, setEditedSystemPrompt] = useState('');
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedTargeting, setEditedTargeting] = useState<Targeting>({});
  const [promptSaveStatus, setPromptSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [promptSaveError, setPromptSaveError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState<PromptVersionInfo[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryVersion, setSelectedHistoryVersion] = useState<PromptVersionInfo | null>(null);
  const [promptImportStatus, setPromptImportStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const promptFileInputRef = useRef<HTMLInputElement>(null);
  const [isExportingPrompts, setIsExportingPrompts] = useState(false);
  const [targetingExpanded, setTargetingExpanded] = useState(false);
  const [placeholdersExpanded, setPlaceholdersExpanded] = useState(false);
  const [assessmentExpanded, setAssessmentExpanded] = useState(false);
  const [editedAssessmentField, setEditedAssessmentField] = useState('');
  const [editedAssessmentSourceType, setEditedAssessmentSourceType] = useState('');
  // Drives scorer dispatch — see docs/FeatureDesigns/AssessmentAndMarking/ScorerDispatch.md.
  // Empty string = unset; required for analysis prompts on save.
  const [editedOutputSchemaType, setEditedOutputSchemaType] = useState<OutputSchemaType | ''>('');
  type EditorTab = 'metadata' | 'user' | 'system' | 'schema';
  const [editorTab, setEditorTab] = useState<EditorTab>('metadata');
  // Schema editor: empty text = no override (use canonical or none).
  // Non-empty text = override (parsed on save).
  const [editedJsonSchemaText, setEditedJsonSchemaText] = useState('');
  const [jsonSchemaError, setJsonSchemaError] = useState<string | null>(null);
  // Per-prompt strict-mode toggle. `undefined` = inherit (capability default).
  // Schema authors disable this when the schema has open-ended objects (e.g.
  // `additionalProperties: true`) that OpenAI strict mode would reject.
  const [editedUseStrictJsonSchema, setEditedUseStrictJsonSchema] = useState<boolean | undefined>(undefined);

  const { data: prompts, isLoading } = useQuery({
    queryKey: ['prompts', 'withTemplates'],
    queryFn: () => adminApi.getPrompts({ includeTemplate: true }),
  });

  const { data: canonicalSchemas } = useQuery({
    queryKey: ['canonical-schemas'],
    queryFn: adminApi.getCanonicalSchemas,
    staleTime: Infinity,
  });

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });

  const activePromptIds = settings?.activePromptIds || [];

  const selectedPrompt = prompts?.find((p: PromptInfo) => p.id === selectedPromptId) || null;

  const settingsMutation = useMutation({
    mutationFn: (newSettings: Partial<AppSettings>) => adminApi.updateSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const promptMutation = useMutation({
    mutationFn: (prompt: { id?: string; name: string; description: string; userPrompt: string; systemPrompt?: string; type?: PromptType; targeting?: Targeting; assessmentField?: string; assessmentSourceType?: string; jsonSchema?: { name: string; schema: object }; useStrictJsonSchema?: boolean; outputSchemaType?: OutputSchemaType }) =>
      adminApi.savePrompt(prompt),
    onMutate: () => {
      setPromptSaveStatus('saving');
      setPromptSaveError(null);
    },
    onSuccess: (result) => {
      setPromptSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      if (isCreatingNew && result?.id) {
        setSelectedPromptId(result.id);
        setIsCreatingNew(false);
      }
      setIsEditing(false);
      setTimeout(() => setPromptSaveStatus('idle'), 2000);
    },
    onError: (error: Error) => {
      setPromptSaveStatus('error');
      setPromptSaveError(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (promptId: string) => adminApi.deletePrompt(promptId),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Remove from active prompts if it was active
      if (activePromptIds.includes(deletedId)) {
        const newActiveIds = activePromptIds.filter(id => id !== deletedId);
        settingsMutation.mutate({ activePromptIds: newActiveIds });
      }
      setSelectedPromptId(null);
      setIsEditing(false);
    },
  });

  const deleteVersionMutation = useMutation({
    mutationFn: ({ promptId, version }: { promptId: string; version: number }) =>
      adminApi.deletePromptVersion(promptId, version),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      if (result.promptDeleted) {
        setSelectedPromptId(null);
      } else {
        setSelectedHistoryVersion(null);
        if (selectedPromptId) loadVersionHistory(selectedPromptId);
      }
    },
  });

  const promptImportMutation = useMutation({
    mutationFn: (promptsToImport: PromptInfo[]) => adminApi.importPrompts(promptsToImport),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      setPromptImportStatus({ type: 'success', message: `Imported ${result.imported} prompts` });
      setTimeout(() => setPromptImportStatus(null), 5000);
    },
    onError: (error: Error) => {
      setPromptImportStatus({ type: 'error', message: error.message });
    },
  });

  const handleToggleActive = async (promptId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't select prompt when clicking checkbox
    const isActive = activePromptIds.includes(promptId);
    if (isActive) {
      // Deactivating — warn if any live pipeline config still references it.
      try {
        const usage = await adminApi.getPromptPipelineUsage(promptId);
        if (usage.configs.length > 0) {
          const list = usage.configs
            .map(c => `• ${c.name} (${c.operations.join(', ')})`)
            .join('\n');
          const ok = confirm(
            `This prompt is currently used in ${usage.configs.length} pipeline config(s):\n\n${list}\n\n` +
            `Deactivating it will leave those configs referencing an inactive prompt. Continue?`
          );
          if (!ok) return;
        }
      } catch (err) {
        // Don't block toggle if the usage check fails — log and continue.
        console.warn('Pipeline usage check failed; proceeding with deactivation', err);
      }
    }
    const newActiveIds = isActive
      ? activePromptIds.filter(id => id !== promptId)
      : [...activePromptIds, promptId];
    settingsMutation.mutate({ activePromptIds: newActiveIds });
  };

  const handleExportPrompts = async () => {
    if (!prompts) return;
    setIsExportingPrompts(true);
    try {
      const promptsWithHistory = await Promise.all(
        prompts.map(async (prompt) => {
          try {
            const history = await adminApi.getPromptHistory(prompt.id);
            return { ...prompt, versions: history };
          } catch { return { ...prompt, versions: [] }; }
        })
      );
      const blob = new Blob([JSON.stringify({ prompts: promptsWithHistory }, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prompts-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingPrompts(false);
    }
  };

  const handlePromptFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      if (data.prompts) promptImportMutation.mutate(data.prompts);
    } catch (err) {
      setPromptImportStatus({ type: 'error', message: 'Invalid file' });
    }
    if (promptFileInputRef.current) promptFileInputRef.current.value = '';
  };

  const loadVersionHistory = async (promptId: string) => {
    setHistoryLoading(true);
    try {
      setVersionHistory(await adminApi.getPromptHistory(promptId));
    } catch { setVersionHistory([]); }
    finally { setHistoryLoading(false); }
  };

  const selectPrompt = (prompt: PromptInfo) => {
    setSelectedPromptId(prompt.id);
    setIsCreatingNew(false);
    // Preserve the current edit/view mode when switching prompts. This makes
    // moving through several prompts in edit mode (e.g. setting outputSchemaType
    // on each) fast — the user doesn't have to click Edit again on every one.
    // Unsaved edits to the previous prompt are discarded silently; cancelEdit
    // is the explicit way to bail.
    setEditedUserPrompt(prompt.userPrompt || '');
    setEditedSystemPrompt(prompt.systemPrompt || '');
    setEditedName(prompt.name);
    setEditedDescription(prompt.description);
    setEditedTargeting(prompt.targeting || {});
    setEditedAssessmentField(prompt.assessmentField || '');
    setEditedAssessmentSourceType(prompt.assessmentSourceType || '');
    setEditedJsonSchemaText(prompt.jsonSchema ? JSON.stringify(prompt.jsonSchema, null, 2) : '');
    setEditedOutputSchemaType(prompt.outputSchemaType || '');
    setEditedUseStrictJsonSchema(prompt.useStrictJsonSchema);
    setJsonSchemaError(null);
    setShowHistory(false);
    setVersionHistory([]);
    setSelectedHistoryVersion(null);
    setPromptSaveStatus('idle');
  };

  const getDefaultTemplate = (type: PromptType): string => {
    if (stepTypeFamily(type) === 'hint') return DEFAULT_HINT_TEMPLATE;
    if (type === 'extract_steps') return DEFAULT_EXTRACT_STEPS_TEMPLATE;
    if (type === 'textchat_greet') return DEFAULT_TEXTCHAT_GREET_TEMPLATE;
    if (type === 'textchat_guide') return DEFAULT_TEXTCHAT_GUIDE_TEMPLATE;
    if (type === 'textchat_summarize') return DEFAULT_TEXTCHAT_SUMMARIZE_TEMPLATE;
    if (type === 'concept_lab') return DEFAULT_CONCEPT_LAB_TEMPLATE;
    return DEFAULT_TEMPLATE;
  };

  const startCreating = (type: PromptType) => {
    setSelectedPromptId(null);
    setIsCreatingNew(true);
    setCreatingType(type);
    setIsEditing(true);
    setEditedUserPrompt(getDefaultTemplate(type));
    setEditedSystemPrompt('');
    setEditedName('');
    setEditedDescription('');
    setEditedTargeting({});
    setEditedAssessmentField('');
    setEditedAssessmentSourceType('');
    setEditedJsonSchemaText('');
    // Pre-fill the output schema from the canonical PromptType→OutputSchemaType
    // registry. The Metadata form renders this read-only; the schema is
    // implied by the prompt's identity (its position + the job it does).
    setEditedOutputSchemaType(PROMPT_OUTPUT_SCHEMA[type] ?? '');
    setEditedUseStrictJsonSchema(undefined);
    setJsonSchemaError(null);
    setShowHistory(false);
    setSelectedHistoryVersion(null);
    setPromptSaveStatus('idle');
  };

  const cancelEdit = () => {
    if (isCreatingNew) {
      setIsCreatingNew(false);
      setSelectedPromptId(null);
    } else if (selectedPrompt) {
      setIsEditing(false);
      setEditedUserPrompt(selectedPrompt.userPrompt || '');
      setEditedSystemPrompt(selectedPrompt.systemPrompt || '');
      setEditedName(selectedPrompt.name);
      setEditedDescription(selectedPrompt.description);
      setEditedTargeting(selectedPrompt.targeting || {});
      setEditedAssessmentField(selectedPrompt.assessmentField || '');
      setEditedAssessmentSourceType(selectedPrompt.assessmentSourceType || '');
      setEditedJsonSchemaText(selectedPrompt.jsonSchema ? JSON.stringify(selectedPrompt.jsonSchema, null, 2) : '');
      setEditedOutputSchemaType(selectedPrompt.outputSchemaType || '');
      setEditedUseStrictJsonSchema(selectedPrompt.useStrictJsonSchema);
      setJsonSchemaError(null);
    }
    setPromptSaveStatus('idle');
  };

  const toggleTargetingOption = (field: 'classes' | 'subjects' | 'difficultyLevels' | 'userProficiency' | 'targetExams' | 'questionTypes', value: string) => {
    if (field === 'userProficiency') {
      const current = editedTargeting.userProficiency || [];
      const typedValue = value as 'low' | 'medium' | 'high';
      const newValues = current.includes(typedValue) ? current.filter(v => v !== typedValue) : [...current, typedValue];
      setEditedTargeting({ ...editedTargeting, userProficiency: newValues.length > 0 ? newValues : undefined });
    } else if (field === 'questionTypes') {
      const current = editedTargeting.questionTypes || [];
      const typedValue = value as 'MCQ' | 'NUMERICAL' | 'FREEFORM';
      const newValues = current.includes(typedValue) ? current.filter(v => v !== typedValue) : [...current, typedValue];
      setEditedTargeting({ ...editedTargeting, questionTypes: newValues.length > 0 ? newValues : undefined });
    } else if (field === 'targetExams') {
      const current = editedTargeting.targetExams || [];
      const newValues = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      setEditedTargeting({ ...editedTargeting, targetExams: newValues.length > 0 ? newValues : undefined });
    } else {
      const current = editedTargeting[field] || [];
      const newValues = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      setEditedTargeting({ ...editedTargeting, [field]: newValues.length > 0 ? newValues : undefined });
    }
  };

  const getTargetingSummary = (targeting?: Targeting): string => {
    if (!targeting) return 'All questions';
    const parts: string[] = [];
    if (targeting.subjects?.length) parts.push(targeting.subjects.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('/'));
    if (targeting.targetExams?.length) parts.push(targeting.targetExams.join('/'));
    if (targeting.userProficiency?.length) parts.push(targeting.userProficiency.map(p => p === 'low' ? 'Beginner' : p === 'medium' ? 'Intermediate' : 'Advanced').join('/'));
    if (targeting.questionTypes?.length) parts.push(targeting.questionTypes.join('/'));
    return parts.length > 0 ? parts.join(' \u2022 ') : 'All questions';
  };

  const handleSave = () => {
    if (!editedName.trim() || !editedUserPrompt.trim()) return;

    // Parse jsonSchema if author provided one. Empty/whitespace = no schema (opt-in).
    let parsedJsonSchema: { name: string; schema: object } | undefined;
    const trimmedSchemaText = editedJsonSchemaText.trim();
    if (trimmedSchemaText) {
      try {
        const obj = JSON.parse(trimmedSchemaText);
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
          throw new Error('Must be a JSON object with `name` and `schema` fields');
        }
        if (typeof obj.name !== 'string' || !obj.name.trim()) {
          throw new Error('`name` must be a non-empty string');
        }
        if (!obj.schema || typeof obj.schema !== 'object' || Array.isArray(obj.schema)) {
          throw new Error('`schema` must be a JSON Schema object');
        }
        parsedJsonSchema = { name: obj.name, schema: obj.schema };
        setJsonSchemaError(null);
      } catch (err) {
        setJsonSchemaError(err instanceof Error ? err.message : String(err));
        setEditorTab('schema');
        return;
      }
    } else {
      setJsonSchemaError(null);
    }

    const cleanTargeting: Targeting = {};
    if (editedTargeting.classes?.length) cleanTargeting.classes = editedTargeting.classes;
    if (editedTargeting.subjects?.length) cleanTargeting.subjects = editedTargeting.subjects;
    if (editedTargeting.difficultyLevels?.length) cleanTargeting.difficultyLevels = editedTargeting.difficultyLevels;
    if (editedTargeting.userProficiency?.length) cleanTargeting.userProficiency = editedTargeting.userProficiency;
    if (editedTargeting.targetExams?.length) cleanTargeting.targetExams = editedTargeting.targetExams;
    if (editedTargeting.questionTypes?.length) cleanTargeting.questionTypes = editedTargeting.questionTypes;
    if (editedTargeting.moduleIds?.length) cleanTargeting.moduleIds = editedTargeting.moduleIds;

    const saveType = (isCreatingNew ? creatingType : selectedPrompt?.type) as PromptType | undefined;
    // Output schema is derived from the prompt's type via PROMPT_OUTPUT_SCHEMA.
    // Honor a previously-stored explicit value (legacy data) so we don't
    // strip it on save, but prefer the canonical derivation when none.
    const derivedSchema = saveType ? PROMPT_OUTPUT_SCHEMA[saveType] ?? undefined : undefined;
    const resolvedOutputSchemaType = (editedOutputSchemaType || derivedSchema) as OutputSchemaType | undefined;

    promptMutation.mutate({
      id: isCreatingNew ? undefined : selectedPromptId || undefined,
      name: editedName,
      description: editedDescription,
      userPrompt: editedUserPrompt,
      systemPrompt: editedSystemPrompt.trim() || undefined,
      type: saveType,
      targeting: Object.keys(cleanTargeting).length > 0 ? cleanTargeting : undefined,
      assessmentField: editedAssessmentField.trim() || undefined,
      assessmentSourceType: editedAssessmentSourceType.trim() || undefined,
      jsonSchema: parsedJsonSchema,
      useStrictJsonSchema: editedUseStrictJsonSchema,
      outputSchemaType: resolvedOutputSchemaType,
    });
  };

  const handleDelete = () => {
    if (!selectedPromptId) return;
    if (confirm(`Delete "${selectedPrompt?.name}" and all versions? This cannot be undone.`)) {
      deleteMutation.mutate(selectedPromptId);
    }
  };

  /** "Promote to git" — fetches the active version's serialized .prompt.md
   *  and triggers a browser download. The admin then drops the file into
   *  the local checkout under Backend/functions/prompts/, commits, and the
   *  next deploy's sync-prompts converts the override to source:'git'. */
  const handlePromoteToFile = async () => {
    if (!selectedPromptId) return;
    try {
      const res = await adminApi.promotePromptToFile(selectedPromptId);
      const blob = new Blob([res.content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Failed to fetch prompt file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  /** Copy the selected prompt as a new prompt. `targetType` lets the admin
   *  retarget the copy to a different prompt type (e.g. retarget an
   *  analyze_image_text_objective prompt down to analyze_text_objective).
   *  Defaults to the source prompt's own type when not provided. */
  const handleCopy = (targetType?: PromptType) => {
    if (!selectedPrompt) return;
    const newType: PromptType = targetType
      ?? (selectedPrompt.type as PromptType)
      ?? 'analyze_image_text_objective';
    // Set up as a new prompt with copied data
    setSelectedPromptId(null);
    setIsCreatingNew(true);
    setCreatingType(newType);
    setIsEditing(true);
    setEditedName(`${selectedPrompt.name} (Copy)`);
    setEditedDescription(selectedPrompt.description);
    setEditedUserPrompt(selectedPrompt.userPrompt || '');
    setEditedSystemPrompt(selectedPrompt.systemPrompt || '');
    setEditedTargeting(selectedPrompt.targeting || {});
    setEditedAssessmentField(selectedPrompt.assessmentField || '');
    setEditedAssessmentSourceType(selectedPrompt.assessmentSourceType || '');
    setEditedJsonSchemaText(selectedPrompt.jsonSchema ? JSON.stringify(selectedPrompt.jsonSchema, null, 2) : '');
    setEditedUseStrictJsonSchema(selectedPrompt.useStrictJsonSchema);
    setJsonSchemaError(null);
    setShowHistory(false);
    setSelectedHistoryVersion(null);
    setPromptSaveStatus('idle');
  };

  const highlightPlaceholders = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = /\{\{[A-Za-z][A-Za-z0-9_.]*\}\}/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      parts.push(<span key={match.index} className="prompts-placeholder-highlight">{match[0]}</span>);
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
  };

  const currentType = isCreatingNew ? creatingType : selectedPrompt?.type || 'analyze_image_text_objective';
  const placeholders = getPromptInputMeta(currentType as PromptType);

  // Observe the list panel's width so we can drop low-priority table columns
  // (Updated → Targeting → schema-line) as it narrows. Tracked imperatively
  // because the panel is sized via flex on its parent — there is no static
  // breakpoint we could hit with a CSS media query.
  useEffect(() => {
    const el = listPanelRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setListPanelWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // NOTE: the three useMemo hooks below MUST run on every render, including
  // when pageTab is not 'prompts' — otherwise React's hook-count check fails
  // (error #310 "Rendered more hooks than during the previous render") when
  // the user clicks a sibling tab. Keep them above the early returns.

  /** Filters + sorts the loaded prompt list locally — no new API. The full
   *  list comes from `getPrompts` and is small enough to filter client-side. */
  const filteredPrompts = useMemo<PromptInfo[]>(() => {
    const all: PromptInfo[] = prompts || [];
    const { type, schema, status, search } = tableFilters;
    const q = search.trim().toLowerCase();
    const filtered = all.filter((p) => {
      if (type && p.type !== type) return false;
      if (schema) {
        // A prompt's effective schema is its stored value, falling back to
        // the canonical PROMPT_OUTPUT_SCHEMA[type]. The Schema filter
        // matches against the effective value so prompts that haven't been
        // explicitly stamped still surface under the right bucket.
        const eff = p.outputSchemaType || PROMPT_OUTPUT_SCHEMA[p.type];
        if (eff !== schema) return false;
      }
      if (status === 'active' && !activePromptIds.includes(p.id)) return false;
      if (status === 'inactive' && activePromptIds.includes(p.id)) return false;
      if (q) {
        const hay = `${p.name} ${p.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = tableSort.sortOrder === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (tableSort.sortBy) {
        case 'name':    cmp = a.name.localeCompare(b.name); break;
        case 'type':    cmp = (PROMPT_TYPE_SORT_INDEX[a.type] ?? 999) - (PROMPT_TYPE_SORT_INDEX[b.type] ?? 999); break;
        case 'updated': cmp = (a.updatedAt || '').localeCompare(b.updatedAt || ''); break;
        default:        cmp = 0;
      }
      // Tiebreaker: stable secondary sort by name asc so the type-grouped
      // feel is preserved when sorting on the default Type column.
      if (cmp === 0 && tableSort.sortBy !== 'name') cmp = a.name.localeCompare(b.name);
      return cmp * dir;
    });
  }, [prompts, tableFilters, tableSort, activePromptIds]);

  // Keep the detail pane in sync with the filtered list. When a filter or
  // search hides the currently-selected prompt, fall back to the first
  // visible row instead of leaving the detail showing an item the user
  // can no longer see in the list. We never override an active edit/create
  // session — the user is in the middle of changes and shouldn't be
  // redirected.
  useEffect(() => {
    if (isEditing || isCreatingNew) return;
    const inList = !!selectedPromptId && filteredPrompts.some(p => p.id === selectedPromptId);
    if (inList) return;
    const first = filteredPrompts[0];
    setSelectedPromptId(first ? first.id : null);
  }, [filteredPrompts, selectedPromptId, isEditing, isCreatingNew]);

  const filterDefs = useMemo<FilterDef[]>(() => [
    {
      // Nested under the operation that runs each prompt — see
      // PROMPT_TYPE_GROUPED_OPTIONS. A flat 32-item list is unscannable.
      key: 'type', kind: 'select', placeholder: 'All Types',
      options: PROMPT_TYPE_GROUPED_OPTIONS,
    },
    {
      key: 'schema', kind: 'select', placeholder: 'All Schemas',
      options: OUTPUT_SCHEMA_OPTIONS.map(s => ({ value: s, label: s })),
    },
    {
      key: 'status', kind: 'select', placeholder: 'All Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ], []);

  const promptColumns = useMemo<ColumnDef<PromptInfo>[]>(() => {
    // Responsive column set. Priorities (low → high, drop in this order as
    // the panel shrinks):
    //   1. Updated      — least essential, dropped first
    //   2. Targeting    — long, dropped at narrower widths
    //   3. Type schema  — collapse the (SchemaName) sub-line into the type cell
    // Name + Type always render. (Active state lives on the right detail
    // panel as a toggle button now, not as a table column.) Thresholds are
    // eyeballed for a 50/50 split on common laptop/desktop screens.
    const w = listPanelWidth;
    const showUpdated   = w === 0 || w >= 800;
    const showTargeting = w === 0 || w >= 620;
    const showSchemaSubline = w === 0 || w >= 480;

    const cols: ColumnDef<PromptInfo>[] = [
      {
        key: 'name', header: 'Name', sortable: true,
        render: (p) => (
          <span className="prompts-col-name">
            {p.name}
            <span className="prompts-col-name-version">v{p.version}</span>
            <SourceBadge
              compact
              source={p.source}
              gitSha={p.gitSha}
              activatedAt={p.activatedAt}
              activatedBy={p.activatedBy}
              reason={p.overrideReason}
            />
          </span>
        ),
      },
      {
        key: 'type', header: 'Type', sortable: true,
        render: (p) => {
          const eff = p.outputSchemaType || PROMPT_OUTPUT_SCHEMA[p.type];
          return (
            <span className="prompts-col-type">
              <code>{p.type}</code>
              {eff && showSchemaSubline && <span className="prompts-col-type-schema">({eff})</span>}
            </span>
          );
        },
      },
    ];
    if (showTargeting) {
      cols.push({
        key: 'targeting', header: 'Targeting',
        render: (p) => {
          // One line per targeting facet — keeps each axis (subject/exam/
          // proficiency/etc.) visually distinct in a narrow column. The
          // bulleted single-line form lives in getTargetingSummary and is
          // still used in the right detail panel where horizontal space
          // is plentiful.
          const t = p.targeting;
          if (!t) return <span className="prompts-col-targeting prompts-col-targeting--empty">All questions</span>;
          const lines: string[] = [];
          if (t.subjects?.length) lines.push(t.subjects.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('/'));
          if (t.targetExams?.length) lines.push(t.targetExams.join('/'));
          if (t.classes?.length) lines.push(`Class ${t.classes.join('/')}`);
          if (t.difficultyLevels?.length) lines.push(t.difficultyLevels.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join('/'));
          if (t.userProficiency?.length) lines.push(t.userProficiency.map(u => u === 'low' ? 'Beginner' : u === 'medium' ? 'Intermediate' : 'Advanced').join('/'));
          if (t.questionTypes?.length) lines.push(t.questionTypes.join('/'));
          if (lines.length === 0) return <span className="prompts-col-targeting prompts-col-targeting--empty">All questions</span>;
          return (
            <span className="prompts-col-targeting">
              {lines.map((l, i) => <span key={i} className="prompts-col-targeting-line">{l}</span>)}
            </span>
          );
        },
      });
    }
    if (showUpdated) {
      cols.push({
        key: 'updated', header: 'Updated', sortable: true,
        render: (p) => p.updatedAt ? <span className="prompts-col-updated" title={p.updatedAt}>{formatDateMonthDay(p.updatedAt)}</span> : <span className="prompts-col-updated">—</span>,
      });
    }
    return cols;
  }, [activePromptIds, listPanelWidth]);

  const displayTemplate = selectedHistoryVersion?.userPrompt || editedUserPrompt || selectedPrompt?.userPrompt || '';
  const displayName = selectedHistoryVersion?.name || (isEditing ? editedName : selectedPrompt?.name) || '';

  // ── History diff ────────────────────────────────────────────────────────
  // Browsing history is about "what changed in this version", so each tab
  // shows the selected version against the one before it rather than the
  // version's contents in isolation. `versionHistory` is ordered newest-first
  // for display, so find the predecessor by version number rather than by
  // position — an archived gap would make an index walk pick the wrong one.
  const previousVersion = useMemo(() => {
    if (!selectedHistoryVersion) return null;
    return versionHistory
      .filter(v => v.version < selectedHistoryVersion.version)
      .sort((a, b) => b.version - a.version)[0] ?? null;
  }, [versionHistory, selectedHistoryVersion]);
  const isDiffMode = showHistory && !isEditing && !!selectedHistoryVersion;
  const schemaText = (v?: PromptVersionInfo | null) =>
    v?.jsonSchema ? JSON.stringify(v.jsonSchema, null, 2) : '';
  /** The metadata tab's diff subject. The prompt bodies and the schema each own
   *  a tab of their own, so they are excluded here — this is everything else
   *  that can change between versions, including the targeting that used to be
   *  shown as a static "Target:" line with no indication of what moved. */
  const metadataFacet = (v: PromptVersionInfo) => ({
    name: v.name,
    description: v.description,
    targeting: v.targeting ?? null,
    assessmentField: v.assessmentField ?? null,
    assessmentSourceType: v.assessmentSourceType ?? null,
    outputSchemaType: v.outputSchemaType ?? null,
    useStrictJsonSchema: v.useStrictJsonSchema ?? null,
    source: v.source ?? null,
    gitSha: v.gitSha ?? null,
  });

  if (isLoading) return <div className="prompts-loading">Loading prompts...</div>;

  if (pageTab === 'pipelines' || pageTab === 'cost-caps' || pageTab === 'providers' || pageTab === 'tiered-sets' || pageTab === 'parallel-sets') {
    return (
      <div className="prompts-page-wrapper split-layout">
      <TabStrip
        className="prompts-page-tabs"
        ariaLabel="Prompt configuration views"
        active={pageTab}
        onChange={setPageTab}
        tabs={PROMPTS_PAGE_TABS}
      />
        <div className="prompts-page-tab-content">
          {pageTab === 'pipelines' && <Pipelines onNavigate={goToProviderTab} />}
          {pageTab === 'cost-caps' && <CostCaps />}
          {pageTab === 'providers' && <Providers initialSearch={providerFocus} />}
          {pageTab === 'tiered-sets' && <ProviderTieredSets />}
          {pageTab === 'parallel-sets' && <ProviderParallelSets />}
        </div>
      </div>
    );
  }

  // When this page is reached via an internal link (e.g. the prompt-link
  // icon on a runs table), render a contextual back button at the top so
  // the student can return to the source page. Hidden on direct/bookmark
  // visits — no `returnTo` means no internal history to pop.
  const returnTo = useReturnLocation();

  return (
    <>
    <div className="prompts-page-wrapper split-layout">
      {returnTo && (
        <div className="prompts-page-back" style={{ padding: '8px 16px 0' }}>
          <BackButton />
        </div>
      )}
      <TabStrip
        className="prompts-page-tabs"
        ariaLabel="Prompt configuration views"
        active={pageTab}
        onChange={setPageTab}
        tabs={PROMPTS_PAGE_TABS}
      />
    <div className="prompts-page">
      {/* Left Panel - List */}
      <div className="prompts-list-panel" ref={listPanelRef}>
        {promptImportStatus && (
          <div className="prompts-filters">
            <div className={`prompts-import-status ${promptImportStatus.type}`}>
              {promptImportStatus.message}
              <button onClick={() => setPromptImportStatus(null)}>x</button>
            </div>
          </div>
        )}

        <div className="prompts-actions-row">
          <button
            className="prompts-list-btn primary"
            onClick={() => setCreateMenuOpen(v => !v)}
            title="Create a new prompt"
          >
            Create ▾
          </button>
          <button className="prompts-list-btn secondary" onClick={handleExportPrompts} disabled={isExportingPrompts}>Export</button>
          <button className="prompts-list-btn secondary" onClick={() => promptFileInputRef.current?.click()}>Import</button>
          <input ref={promptFileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handlePromptFileSelect} />
          {createMenuOpen && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                onClick={() => setCreateMenuOpen(false)}
              />
              {/* Grouped by the operation that runs each type, and each type
                  listed ONCE — creating is a single act, unlike filtering. */}
              <div className="prompts-create-popover">
                {PROMPT_TYPE_GROUPS.map((run, i) => (
                  <div key={run.group ?? `flat-${i}`} className="prompts-create-popover-group">
                    {/* No heading for the single-prompt operations: it would
                        repeat the only item under it. */}
                    {run.group && <div className="prompts-create-popover-heading">{run.group}</div>}
                    {run.options.map(o => (
                      <button
                        key={o.value}
                        type="button"
                        className="prompts-create-popover-item"
                        onClick={() => { setCreateMenuOpen(false); startCreating(o.value); }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <SearchFilterBar
          filters={filterDefs}
          values={tableFilters as unknown as Record<string, string>}
          onChange={(values) => setTableFilters({
            type:   values.type   ?? '',
            schema: values.schema ?? '',
            status: values.status ?? '',
            search: values.search ?? '',
          })}
          searchPlaceholder="Search name or description"
          hideFetch
        />

        {isCreatingNew && (
          <div className="prompts-list-creating-banner">
            <span>Creating new {promptTypeLabel(creatingType)} prompt…</span>
          </div>
        )}

        <div className="prompts-table-scroll">
        <DataTable<PromptInfo>
          columns={promptColumns}
          data={filteredPrompts}
          rowKey={(p) => p.id}
          onRowClick={(p) => selectPrompt(p)}
          rowClassName={(p) => [
            selectedPromptId === p.id && !isCreatingNew ? 'dt-row--selected' : '',
            activePromptIds.includes(p.id) ? 'prompts-row--active' : '',
          ].filter(Boolean).join(' ')}
          sort={tableSort}
          onSortChange={setTableSort}
          loading={isLoading}
          emptyMessage="No prompts match the current filters"
          compact
        />
        </div>
      </div>

      {/* Right Panel - Detail */}
      <div className="prompts-detail-panel">
        {!selectedPrompt && !isCreatingNew ? (
          <div className="prompts-detail-empty">Select a prompt to view details</div>
        ) : (
          <>
            {/* Header */}
            <div className="prompts-detail-header">
              <div className="prompts-detail-header-left">
                <h2>{isCreatingNew ? `New ${promptTypeLabel(creatingType)} Prompt` : displayName}</h2>
                <div className="prompts-detail-header-badges">
                  {!isCreatingNew && selectedPrompt && (
                    <>
                      <span className="prompts-badge version">{selectedPrompt.id}.{selectedHistoryVersion?.version || selectedPrompt.version}</span>
                      {activePromptIds.includes(selectedPrompt.id) && <span className="prompts-badge active">Active</span>}
                      {selectedHistoryVersion ? (
                        <SourceBadge
                          source={selectedHistoryVersion.source}
                          gitSha={selectedHistoryVersion.gitSha}
                          activatedAt={selectedHistoryVersion.updatedAt}
                        />
                      ) : (
                        <SourceBadge
                          source={selectedPrompt.source}
                          gitSha={selectedPrompt.gitSha}
                          activatedAt={selectedPrompt.activatedAt}
                          activatedBy={selectedPrompt.activatedBy}
                          reason={selectedPrompt.overrideReason}
                        />
                      )}
                    </>
                  )}
                  {stepTypeFamily(currentType as PromptType) === 'hint' && <span className="prompts-badge hint">Hint</span>}
                  {stepTypeFamily(currentType as PromptType) === 'textchat' && <span className="prompts-badge hint">TextChat</span>}
                  {selectedHistoryVersion && <span className="prompts-badge history">History</span>}
                </div>
              </div>
              <div className="prompts-detail-header-actions">
                {!isCreatingNew && !isEditing && !selectedHistoryVersion && selectedPrompt && (
                  <>
                    <button
                      className={`prompts-detail-btn ${activePromptIds.includes(selectedPrompt.id) ? 'danger-secondary' : 'success'}`}
                      onClick={(e) => handleToggleActive(selectedPrompt.id, e as unknown as React.MouseEvent)}
                      title={activePromptIds.includes(selectedPrompt.id) ? 'Currently active in pipelines — click to disable' : 'Currently disabled — click to activate for pipelines'}
                    >
                      {activePromptIds.includes(selectedPrompt.id) ? 'Disable' : 'Activate'}
                    </button>
                    <button className="prompts-detail-btn secondary" onClick={() => { loadVersionHistory(selectedPromptId!); setShowHistory(!showHistory); }}>
                      {historyLoading ? 'Loading...' : showHistory ? 'Hide History' : 'History'}
                    </button>
                    <button
                      className="prompts-detail-btn secondary"
                      onClick={() => {
                        setCopyDialogTarget((selectedPrompt?.type as PromptType) || null);
                        setShowCopyDialog(true);
                      }}
                      title="Create a copy as a new prompt"
                    >
                      Copy
                    </button>
                    <button className="prompts-detail-btn primary" onClick={() => setIsEditing(true)}>Edit</button>
                    {selectedPrompt && selectedPrompt.version > 1 && (
                      <button
                        className="prompts-detail-btn danger-secondary"
                        onClick={() => {
                          if (confirm(`Delete version ${selectedPrompt.version} of "${selectedPrompt.name}"? This will roll back to version ${selectedPrompt.version - 1}.`)) {
                            deleteVersionMutation.mutate({ promptId: selectedPromptId!, version: selectedPrompt.version });
                          }
                        }}
                        disabled={deleteVersionMutation.isPending}
                        title="Delete current version and roll back to previous"
                      >
                        Delete v{selectedPrompt.version}
                      </button>
                    )}
                    <button
                      className="prompts-detail-btn danger"
                      onClick={handleDelete}
                      disabled={deleteMutation.isPending}
                      title="Delete prompt and all versions"
                    >
                      Delete All
                    </button>
                  </>
                )}
                {selectedHistoryVersion && (
                  <>
                    <button className="prompts-detail-btn danger" onClick={() => deleteVersionMutation.mutate({ promptId: selectedPromptId!, version: selectedHistoryVersion.version })}>
                      Delete Version
                    </button>
                    <button className="prompts-detail-btn primary" onClick={() => { setEditedUserPrompt(selectedHistoryVersion.userPrompt); setEditedSystemPrompt(selectedHistoryVersion.systemPrompt || ''); setEditedName(selectedHistoryVersion.name); setIsEditing(true); setSelectedHistoryVersion(null); }}>
                      Restore
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Override banner — shown when the active version is an admin
                override of the git-tracked content. The admin can promote the
                override back to git via the download button. */}
            {selectedPrompt && !isCreatingNew && !isEditing && selectedPrompt.source === 'admin' && (
              <div
                style={{
                  margin: '12px 24px 0 24px',
                  padding: '12px 16px',
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: 6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
                <div style={{ fontSize: 13, lineHeight: 1.5, color: '#fbbf24' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Admin override active
                  </div>
                  <div style={{ color: '#d4d4d8' }}>
                    {selectedPrompt.activatedAt && <>Since {format(new Date(selectedPrompt.activatedAt), 'MMM d, yyyy HH:mm')}</>}
                    {selectedPrompt.activatedBy && <> · by {selectedPrompt.activatedBy}</>}
                    {selectedPrompt.overrideReason && <> · <em style={{ opacity: 0.85 }}>{selectedPrompt.overrideReason}</em></>}
                  </div>
                  <div style={{ color: '#a1a1aa', marginTop: 6, fontSize: 12 }}>
                    Promote downloads a <code>.prompt.md</code>. Drop it into <code>Backend/functions/prompts/</code> and commit;
                    the next deploy will reconcile this prompt back to <strong>source: git</strong>.
                  </div>
                </div>
                <button
                  className="prompts-detail-btn primary"
                  onClick={handlePromoteToFile}
                  title="Download a .prompt.md of the current admin-override content"
                  style={{ flexShrink: 0 }}
                >
                  Promote to git
                </button>
              </div>
            )}

            {/* Content */}
            <div className="prompts-detail-content">
              {/* Version History */}
              {showHistory && !isEditing && versionHistory.length > 0 && (
                <div className="prompts-history-section">
                  <h4>Version History</h4>
                  <div className="prompts-history-list">
                    {versionHistory.map((v) => (
                      <div
                        key={v.id}
                        className={`prompts-history-item ${selectedHistoryVersion?.id === v.id ? 'selected' : ''} ${v.version === selectedPrompt?.version ? 'current' : ''}`}
                        onClick={() => setSelectedHistoryVersion(v)}
                      >
                        <div className="prompts-history-item-header">
                          <span className="prompts-history-item-version">v{v.version}</span>
                          {v.version === selectedPrompt?.version && <span className="prompts-history-item-current">Current</span>}
                          <SourceBadge compact source={v.source} gitSha={v.gitSha} activatedAt={v.updatedAt} />
                        </div>
                        <span className="prompts-history-item-date">{format(new Date(v.archivedAt || v.updatedAt), 'MMM d, yyyy')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Editor Tabs — also shown while browsing history, where each
                  tab becomes a diff of the selected version against the one
                  before it. */}
              {(!showHistory || isDiffMode) && (
                <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '0 24px', marginTop: 4 }}>
                  {([
                    { key: 'metadata' as const, label: 'Metadata' },
                    { key: 'user' as const, label: 'User Prompt' },
                    { key: 'system' as const, label: 'System Prompt' },
                    { key: 'schema' as const, label: `Schema${(isEditing ? editedJsonSchemaText.trim() : (selectedHistoryVersion?.jsonSchema || selectedPrompt?.jsonSchema)) ? ' •' : ''}` },
                  ]).map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setEditorTab(t.key)}
                      style={{
                        padding: '10px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: editorTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
                        color: editorTab === t.key ? '#fff' : '#9ca3af',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: editorTab === t.key ? 600 : 400,
                        marginBottom: -1,
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {/* History diff — replaces the normal read-only view while a
                  version is selected. Answers "what changed in this version"
                  per tab, instead of showing the version's contents with no
                  reference point. */}
              {isDiffMode && selectedHistoryVersion && (
                <div className="prompts-history-diff">
                  <div className="prompts-history-diff-header">
                    {previousVersion
                      ? <>Changes in <strong>v{selectedHistoryVersion.version}</strong> vs <strong>v{previousVersion.version}</strong></>
                      : <>v{selectedHistoryVersion.version} is the first version — nothing to compare against, showing its contents.</>}
                  </div>

                  {editorTab === 'metadata' && (
                    previousVersion
                      ? <JsonDiffViewer
                          before={metadataFacet(previousVersion)}
                          after={metadataFacet(selectedHistoryVersion)}
                          title={`v${previousVersion.version} → v${selectedHistoryVersion.version}`}
                        />
                      : <pre className="prompts-history-diff-pre">{JSON.stringify(metadataFacet(selectedHistoryVersion), null, 2)}</pre>
                  )}

                  {editorTab === 'user' && (
                    previousVersion
                      ? <TextDiffViewer before={previousVersion.userPrompt || ''} after={selectedHistoryVersion.userPrompt || ''} />
                      : <pre className="prompts-history-diff-pre">{selectedHistoryVersion.userPrompt}</pre>
                  )}

                  {editorTab === 'system' && (
                    previousVersion
                      ? <TextDiffViewer before={previousVersion.systemPrompt || ''} after={selectedHistoryVersion.systemPrompt || ''} />
                      : <pre className="prompts-history-diff-pre">{selectedHistoryVersion.systemPrompt || '(no system prompt)'}</pre>
                  )}

                  {editorTab === 'schema' && (
                    previousVersion
                      ? <TextDiffViewer before={schemaText(previousVersion)} after={schemaText(selectedHistoryVersion)} />
                      : <pre className="prompts-history-diff-pre">{schemaText(selectedHistoryVersion) || '(no schema override)'}</pre>
                  )}
                </div>
              )}

              {/* Form (edit mode, metadata tab) */}
              {isEditing && editorTab === 'metadata' && (() => {
                const derivedSchema = PROMPT_OUTPUT_SCHEMA[currentType as PromptType] ?? null;
                const effectiveSchema = editedOutputSchemaType || derivedSchema || '';
                return (
                <div className="prompts-form-section">
                  <div className="prompts-form-row">
                    <label>Name</label>
                    <input type="text" value={editedName} onChange={(e) => setEditedName(e.target.value)} placeholder="Prompt name" />
                  </div>
                  <div className="prompts-form-row">
                    <label>Description</label>
                    <input type="text" value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} placeholder="Short description" />
                  </div>
                  <div className="prompts-form-row">
                    <label>Type</label>
                    <input type="text" value={promptTypeLabel(currentType)} disabled readOnly />
                  </div>
                  <div className="prompts-form-row">
                    <label>Output Schema</label>
                    <input
                      type="text"
                      value={effectiveSchema || '— not applicable —'}
                      disabled
                      readOnly
                      title="Derived from the prompt's type — see PROMPT_OUTPUT_SCHEMA in Backend/shared/src/promptTypes.ts"
                    />
                  </div>
                </div>
                );
              })()}

              {/* Description (view mode, settings tab) */}
              {!isEditing && !isDiffMode && selectedPrompt && editorTab === 'metadata' && (
                <p className="prompts-view-description">{selectedHistoryVersion?.description || selectedPrompt.description}</p>
              )}

              {/* Targeting (edit mode, metadata tab) */}
              {isEditing && editorTab === 'metadata' && (
                <div className="prompts-form-section">
                  <div className="prompts-section-header" onClick={() => setTargetingExpanded(!targetingExpanded)}>
                    <h4>Targeting</h4>
                    <span className={`prompts-section-toggle ${targetingExpanded ? 'expanded' : ''}`}>▶</span>
                  </div>
                  {targetingExpanded && (
                  <div className="prompts-targeting-content">
                  <div className="prompts-targeting-row">
                    <label>Subjects:</label>
                    <div className="prompts-targeting-options">
                      {QUESTION_SUBJECT_OPTIONS.map((s) => (
                        <label key={s} className="prompts-targeting-checkbox">
                          <input type="checkbox" checked={editedTargeting.subjects?.includes(s) || false} onChange={() => toggleTargetingOption('subjects', s)} />
                          <span>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {currentType !== 'extract_steps' && (
                  <>
                  <div className="prompts-targeting-row">
                    <label>Classes:</label>
                    <div className="prompts-targeting-options">
                      {GRADE_OPTIONS.map((c) => (
                        <label key={c} className="prompts-targeting-checkbox">
                          <input type="checkbox" checked={editedTargeting.classes?.includes(c) || false} onChange={() => toggleTargetingOption('classes', c)} />
                          <span>{c}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="prompts-targeting-row">
                    <label>Difficulty:</label>
                    <div className="prompts-targeting-options">
                      {DIFFICULTY_OPTIONS.map((d) => (
                        <label key={d} className="prompts-targeting-checkbox">
                          <input type="checkbox" checked={editedTargeting.difficultyLevels?.includes(d) || false} onChange={() => toggleTargetingOption('difficultyLevels', d)} />
                          <span>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="prompts-targeting-row">
                    <label>Proficiency:</label>
                    <div className="prompts-targeting-options">
                      {PROFICIENCY_OPTIONS.map((p) => (
                        <label key={p} className="prompts-targeting-checkbox">
                          <input type="checkbox" checked={editedTargeting.userProficiency?.includes(p) || false} onChange={() => toggleTargetingOption('userProficiency', p)} />
                          <span>{p === 'low' ? 'Beginner' : p === 'medium' ? 'Intermediate' : 'Advanced'}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="prompts-targeting-row">
                    <label>Exams:</label>
                    <div className="prompts-targeting-options">
                      {REGISTRABLE_PROGRAMME_OPTIONS.map((e) => (
                        <label key={e} className="prompts-targeting-checkbox">
                          <input type="checkbox" checked={editedTargeting.targetExams?.includes(e) || false} onChange={() => toggleTargetingOption('targetExams', e)} />
                          <span>{e}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="prompts-targeting-row">
                    <label>Question Types:</label>
                    <div className="prompts-targeting-options">
                      {QUESTION_TYPE_OPTIONS.map((q) => (
                        <label key={q} className="prompts-targeting-checkbox">
                          <input type="checkbox" checked={editedTargeting.questionTypes?.includes(q) || false} onChange={() => toggleTargetingOption('questionTypes', q)} />
                          <span>{q}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  </>
                  )}
                  </div>
                  )}
                </div>
              )}

              {/* Assessment Extraction (edit mode, analysis types only, settings tab) */}
              {isEditing && editorTab === 'metadata' && stepTypeFamily(currentType as PromptType) === 'analyze' && (
                <div className="prompts-form-section">
                  <div className="prompts-section-header" onClick={() => setAssessmentExpanded(!assessmentExpanded)}>
                    <h4>Assessment Extraction</h4>
                    <span className={`prompts-section-toggle ${assessmentExpanded ? 'expanded' : ''}`}>▶</span>
                  </div>
                  {assessmentExpanded && (
                  <div>
                  <div className="prompts-form-row">
                    <label>Assessment Field</label>
                    <input type="text" value={editedAssessmentField} onChange={(e) => setEditedAssessmentField(e.target.value)} placeholder="e.g., overall_assessment" />
                  </div>
                  <div className="prompts-form-row">
                    <label>Source Type</label>
                    <input type="text" value={editedAssessmentSourceType} onChange={(e) => setEditedAssessmentSourceType(e.target.value)} placeholder="e.g., llm_analysis" />
                  </div>
                  </div>
                  )}
                </div>
              )}

              {/* Output JSON Schema (schema tab) */}
              {editorTab === 'schema' && !isDiffMode && (() => {
                const canonicalForType = canonicalSchemas?.[currentType as PromptType];
                const promptOverride = selectedHistoryVersion?.jsonSchema || selectedPrompt?.jsonSchema;
                const isOverride = isEditing ? !!editedJsonSchemaText.trim() : !!promptOverride;
                const effective = isOverride
                  ? (isEditing ? null : promptOverride)
                  : canonicalForType;
                const monoBox: React.CSSProperties = {
                  margin: 0,
                  padding: 16,
                  background: 'var(--code-bg)',
                  color: 'var(--code-text)',
                  borderRadius: 8,
                  fontFamily: 'Monaco, Menlo, monospace',
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflow: 'auto',
                };
                return (
                  <div style={{ padding: '16px 24px', flex: '0 0 auto', width: '100%', boxSizing: 'border-box' }}>
                    <div className="prompts-template-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h4 style={{ margin: 0 }}>Output JSON Schema</h4>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 4,
                        background: isOverride ? 'rgba(245,158,11,0.2)' : (canonicalForType ? 'rgba(99,102,241,0.2)' : 'rgba(156,163,175,0.2)'),
                        color: isOverride ? '#fbbf24' : (canonicalForType ? '#a5b4fc' : '#9ca3af'),
                      }}>
                        {isOverride ? 'OVERRIDE' : (canonicalForType ? 'DEFAULT (canonical)' : 'NONE')}
                      </span>
                      {isEditing && (
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                          {isOverride ? (
                            <button
                              type="button"
                              className="prompts-detail-btn secondary"
                              onClick={() => { setEditedJsonSchemaText(''); setJsonSchemaError(null); }}
                              title={canonicalForType ? 'Clear override; fall back to canonical schema for this type' : 'Clear override; no schema enforcement'}
                            >
                              {canonicalForType ? 'Revert to canonical' : 'Clear override'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="prompts-detail-btn secondary"
                              onClick={() => {
                                const seed = canonicalForType
                                  ? JSON.stringify(canonicalForType, null, 2)
                                  : '{\n  "name": "custom_response",\n  "schema": {\n    "type": "object",\n    "additionalProperties": false,\n    "properties": {},\n    "required": []\n  }\n}';
                                setEditedJsonSchemaText(seed);
                                setJsonSchemaError(null);
                              }}
                            >
                              {canonicalForType ? 'Override...' : 'Add custom schema'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.75, margin: '8px 0 12px' }}>
                      {isOverride
                        ? <>This prompt overrides the canonical schema for <code>{currentType}</code>. The model output will be strictly constrained to the JSON below.</>
                        : canonicalForType
                          ? <>Using the canonical schema for <code>{currentType}</code> (defined in <code>typeSchemas.ts</code>). All prompts of this type share it. {isEditing ? 'Click "Override..." to customize for this prompt only.' : ''}</>
                          : <>No canonical schema is defined for <code>{currentType}</code>, and this prompt has no override. The model output is unconstrained. {isEditing ? 'Click "Add custom schema" to define one for this prompt only.' : ''}</>}
                    </div>
                    {effective && (
                      <div style={{ margin: '0 0 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                          <span style={{ color: 'var(--text-secondary)', minWidth: 140 }}>Strict JSON Schema</span>
                          {isEditing ? (
                            <select
                              value={editedUseStrictJsonSchema === undefined ? '' : editedUseStrictJsonSchema ? 'true' : 'false'}
                              onChange={e => setEditedUseStrictJsonSchema(e.target.value === '' ? undefined : e.target.value === 'true')}
                              style={{
                                padding: '6px 10px',
                                fontSize: 13,
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border-color-dark, var(--border-color))',
                                borderRadius: 4,
                                fontFamily: 'inherit',
                              }}
                            >
                              <option value="">Inherit (capability default)</option>
                              <option value="true">On</option>
                              <option value="false">Off</option>
                            </select>
                          ) : (
                            <span style={{ fontWeight: 500 }}>
                              {(selectedHistoryVersion?.useStrictJsonSchema ?? selectedPrompt?.useStrictJsonSchema) === undefined
                                ? 'Inherit'
                                : (selectedHistoryVersion?.useStrictJsonSchema ?? selectedPrompt?.useStrictJsonSchema)
                                  ? 'On' : 'Off'}
                            </span>
                          )}
                        </div>
                        {isEditing && (
                          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4, marginLeft: 150 }}>
                            Turn off when the schema has open-ended objects (e.g. <code>additionalProperties: true</code>).
                          </div>
                        )}
                      </div>
                    )}
                    {isEditing && isOverride ? (
                      <>
                        <textarea
                          value={editedJsonSchemaText}
                          onChange={(e) => { setEditedJsonSchemaText(e.target.value); if (jsonSchemaError) setJsonSchemaError(null); }}
                          spellCheck={false}
                          style={{
                            width: '100%',
                            height: 'calc(100vh - 320px)',
                            minHeight: 320,
                            padding: 16,
                            background: 'var(--code-bg)',
                            color: 'var(--code-text)',
                            border: 'none',
                            borderRadius: 8,
                            fontFamily: 'Monaco, Menlo, monospace',
                            fontSize: 12,
                            lineHeight: 1.5,
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            display: 'block',
                          }}
                        />
                        {jsonSchemaError && (
                          <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>Schema error: {jsonSchemaError}</div>
                        )}
                      </>
                    ) : effective ? (
                      <pre style={{ ...monoBox, maxHeight: 'calc(100vh - 320px)', minHeight: 320 }}>
                        {JSON.stringify(effective, null, 2)}
                      </pre>
                    ) : (
                      <div style={{ padding: '8px 12px', color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '13px' }}>(no schema — model output is unconstrained)</div>
                    )}
                  </div>
                );
              })()}

              {/* Targeting Summary (view mode, settings tab). Hidden while
                  diffing history — the metadata diff covers targeting, and
                  showing a static "Target:" line there said nothing about what
                  the selected version actually changed. */}
              {!isEditing && !isDiffMode && selectedPrompt && editorTab === 'metadata' && (
                <div className="prompts-targeting-summary">
                  <span className="prompts-targeting-summary-label">Target:</span>
                  <span className="prompts-targeting-summary-value">{getTargetingSummary(selectedHistoryVersion?.targeting || selectedPrompt.targeting)}</span>
                  {(selectedPrompt.assessmentField || selectedHistoryVersion?.assessmentField) && (
                    <span className="prompts-targeting-summary-value" style={{ marginLeft: 12 }}>
                      Assessment: <code>{selectedHistoryVersion?.assessmentField || selectedPrompt.assessmentField}</code> / Source: <code>{selectedHistoryVersion?.assessmentSourceType || selectedPrompt.assessmentSourceType}</code>
                    </span>
                  )}
                </div>
              )}

              {/*
                Both prompt sections are plain block containers (NOT flex
                items). The original `.prompts-template-section` class set
                `flex: 1; min-height: 0` and `.prompts-template-editor` set
                `min-height: 525px`, which made two stacked sections fight for
                space and visually overlap. Each section now uses a plain block
                with an explicit height on the textarea.
              */}
              {!isDiffMode && (() => {
                const editorStyle: React.CSSProperties = {
                    width: '100%',
                    padding: 16,
                    background: 'var(--code-bg)',
                    color: 'var(--code-text)',
                    border: 'none',
                    borderRadius: 8,
                    fontFamily: 'Monaco, Menlo, monospace',
                    fontSize: 12,
                    lineHeight: 1.5,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    display: 'block',
                };
                const previewStyle: React.CSSProperties = {
                    margin: 0,
                    padding: 16,
                    background: 'var(--code-bg)',
                    color: 'var(--code-text)',
                    borderRadius: 8,
                    fontFamily: 'Monaco, Menlo, monospace',
                    fontSize: 12,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflow: 'auto',
                };
                return (
                  <>
                    {/* System Prompt (optional) — system tab */}
                    {editorTab === 'system' && (
                    <div style={{ padding: '16px 24px', flex: '0 0 auto', width: '100%', boxSizing: 'border-box' }}>
                      <div className="prompts-template-header">
                        <h4>System Prompt <span style={{ fontWeight: 'normal', color: 'var(--text-tertiary)', fontSize: '12px' }}>(optional — persona / rules / output contract)</span></h4>
                      </div>
                      {isEditing ? (
                        <>
                          <textarea
                            value={editedSystemPrompt}
                            onChange={(e) => setEditedSystemPrompt(e.target.value)}
                            placeholder="Optional. Stable across submissions — sent as instructions/system. Leave blank to send everything as the user prompt."
                            spellCheck={false}
                            style={{ ...editorStyle, height: 'calc(100vh - 280px)', minHeight: 320 }}
                          />
                          {(() => {
                            // Cardinality warning: high-cardinality placeholders
                            // (per-submission values like {{q.text}}, {{std.actualTimeSeconds}}, etc.)
                            // in the system prompt break Anthropic prompt caching.
                            // Each distinct value creates a fresh cache entry with no read amortization,
                            // and writes are billed at 1.25× — so it actively costs more than not caching.
                            // The findHighCardinalityPromptInputs helper lives in @padcanvas/shared/placeholders.ts.
                            const offenders = findHighCardinalityPromptInputs(editedSystemPrompt);
                            if (offenders.length === 0) return null;
                            return (
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: '10px 14px',
                                  borderRadius: 6,
                                  backgroundColor: 'rgba(245, 158, 11, 0.12)',
                                  border: '1px solid rgba(245, 158, 11, 0.35)',
                                  color: '#fbbf24',
                                  fontSize: 13,
                                  lineHeight: 1.5,
                                }}
                              >
                                <strong>⚠ Cache-breaking placeholders in system prompt</strong>
                                <div style={{ marginTop: 4, color: '#fde68a' }}>
                                  These resolve to different values per submission and will defeat
                                  prompt caching (each call writes a fresh cache entry, never reads):
                                </div>
                                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  {offenders.map((p) => (
                                    <code
                                      key={p}
                                      style={{
                                        padding: '2px 6px',
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(245, 158, 11, 0.25)',
                                        color: '#fde68a',
                                        fontSize: 12,
                                      }}
                                    >
                                      {`{{${p}}}`}
                                    </code>
                                  ))}
                                </div>
                                <div style={{ marginTop: 6, color: '#fde68a', fontSize: 12 }}>
                                  Move them to the user prompt, or replace with a static schema description.
                                  Save is allowed — this is a recommendation, not a block.
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      ) : (
                        (selectedHistoryVersion?.systemPrompt || selectedPrompt?.systemPrompt) ? (
                          <pre style={{ ...previewStyle, maxHeight: 'calc(100vh - 280px)', minHeight: 320 }}>
                            {highlightPlaceholders(selectedHistoryVersion?.systemPrompt || selectedPrompt?.systemPrompt || '')}
                          </pre>
                        ) : (
                          <div style={{ padding: '8px 12px', color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '13px' }}>(none)</div>
                        )
                      )}
                    </div>
                    )}

                    {/* User Prompt Template — user tab */}
                    {editorTab === 'user' && (
                    <div style={{ padding: '16px 24px', flex: '0 0 auto', width: '100%', boxSizing: 'border-box' }}>
                      <div className="prompts-template-header">
                        <h4>User Prompt Template</h4>
                        {!isEditing && displayTemplate && (
                          <button
                            className="prompts-detail-btn"
                            title="Download prompt text"
                            onClick={() => {
                              const blob = new Blob([displayTemplate], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${selectedPrompt?.name || 'prompt'}.txt`;
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                          >
                            Download
                          </button>
                        )}
                      </div>
                      {isEditing ? (
                        <>
                          <textarea
                            value={editedUserPrompt}
                            onChange={(e) => setEditedUserPrompt(e.target.value)}
                            spellCheck={false}
                            style={{ ...editorStyle, height: 525 }}
                          />
                          <div className="prompts-placeholders">
                            <div className="prompts-section-header" onClick={() => setPlaceholdersExpanded(!placeholdersExpanded)}>
                              <h5>Placeholders</h5>
                              <span className={`prompts-section-toggle ${placeholdersExpanded ? 'expanded' : ''}`}>▶</span>
                            </div>
                            {placeholdersExpanded && (
                              <div className="prompts-placeholders-list">
                                {placeholders.map((p) => (
                                  <div key={p.name} className="prompts-placeholder-item">
                                    <code>{p.tag}</code>
                                    <span>{p.description}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <pre style={{ ...previewStyle, minHeight: 200 }}>
                          {highlightPlaceholders(displayTemplate)}
                        </pre>
                      )}
                    </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="prompts-detail-footer">
              <div className="prompts-detail-footer-left">
                {promptSaveStatus === 'saving' && <span className="prompts-status saving">Saving...</span>}
                {promptSaveStatus === 'saved' && <span className="prompts-status saved">Saved!</span>}
                {promptSaveStatus === 'error' && (
                  <span className="prompts-status error" title={promptSaveError ?? ''}>{promptSaveError}</span>
                )}
                {!isEditing && !selectedHistoryVersion && <span className="prompts-edit-note">Click Edit to modify</span>}
              </div>
              <div className="prompts-detail-footer-right">
                {isEditing && (
                  <>
                    <button className="prompts-detail-btn secondary" onClick={cancelEdit}>Cancel</button>
                    <button className="prompts-detail-btn primary" onClick={handleSave} disabled={promptMutation.isPending || !editedName.trim() || !editedUserPrompt.trim()}>
                      {isCreatingNew ? 'Create' : `Save (v${(selectedPrompt?.version || 0) + 1})`}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </div>

    {showCopyDialog && selectedPrompt && (
      <div className="modal-overlay">
        <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Copy Prompt</h2>
            <button className="close-btn" onClick={() => setShowCopyDialog(false)}>×</button>
          </div>
          <div className="modal-body">
            <p className="modal-description">
              Copy <strong>{selectedPrompt.name}</strong> ({promptTypeLabel(selectedPrompt.type || '')})
              as a new prompt. Pick the target type for the copy.
            </p>
            <div className="form-group">
              <label>Target Type</label>
              <select
                value={copyDialogTarget ?? ''}
                onChange={(e) => setCopyDialogTarget(e.target.value as PromptType)}
                className="dataset-select"
              >
                {PROMPT_TYPE_GROUPS.map((run, i) => {
                  const items = run.options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ));
                  return run.group
                    ? <optgroup key={run.group} label={run.group}>{items}</optgroup>
                    : <Fragment key={`flat-${i}`}>{items}</Fragment>;
                })}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button className="cancel-btn" onClick={() => setShowCopyDialog(false)}>Cancel</button>
            <button
              className="save-btn"
              onClick={() => {
                if (copyDialogTarget) handleCopy(copyDialogTarget);
                setShowCopyDialog(false);
              }}
              disabled={!copyDialogTarget}
            >
              Copy
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
